/**
 * Pure domain model and storage rules for the Sidebar Favorites feature (PSS-2642).
 *
 * This module deliberately contains no React and no DOM access. It is the seam a
 * future server-backed implementation replaces: the three favorites surfaces talk
 * only to `FavoritesContext`, which in turn relies on these pure helpers.
 */

/**
 * A single favorited page.
 *
 * `pathname` is the page's *identity* (no query string) — two visits to the same
 * page with different filters/sorts are the same favorite. `url` is the *destination*
 * (pathname + query string) captured at creation, so clicking always returns the user
 * to a meaningful state. Identity and destination are intentionally allowed to differ.
 */
export interface Favorite {
  /** Display label; defaults to `document.title` at creation, user-editable. */
  label: string;
  /** Identity — pathname only, no query string. Used for dedup and the star fill state. */
  pathname: string;
  /** Destination — full pathname + query string, captured at creation. */
  url: string;
}

/** Maximum number of favorites a user may pin per entity. */
export const FAVORITES_CAP = 10;

function isValidFavorite(value: unknown): value is Favorite {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.label === 'string' &&
    typeof candidate.pathname === 'string' &&
    typeof candidate.url === 'string'
  );
}

/**
 * Safely deserializes the stored favorites string. Falls back to `[]` on missing or
 * corrupt data, and filters out individual malformed entries so one bad record cannot
 * discard the rest of a user's list.
 */
export function parseFavorites(raw: string | null | undefined): Favorite[] {
  if (raw == null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(isValidFavorite);
}

/** True when a favorite with the given pathname identity already exists. */
export function containsPathname(favorites: Favorite[], pathname: string): boolean {
  return favorites.some((favorite) => favorite.pathname === pathname);
}

/** True when the list has reached the cap and cannot accept another favorite. */
export function isAtCap(favorites: Favorite[]): boolean {
  return favorites.length >= FAVORITES_CAP;
}
